import {
  jsonToMerkleTree,
  findNodeByJsonPath,
} from './json_merkle.js';
import {
  back,
  getLast,
} from '../../../json_path.js';

const handlers = {
  'append': ({
    newNode,
    hashFn,
  }, v) => {
    if (newNode.data.type !== 'array') {
      throw new Error('node type is not array');
    }

    // append v
    newNode.appendChild(jsonToMerkleTree(v, hashFn));
  },

  'prepend': ({
    newNode,
    hashFn,
  }, v) => {
    if (newNode.data.type !== 'array') {
      throw new Error('node type is not array');
    }

    // prepend v
    newNode.prependChild(jsonToMerkleTree(v, hashFn));
  },

  'updateAt': ({
    newNode,
    hashFn,
  }, index, value) => {
    if (newNode.data.type !== 'array') {
      throw new Error('node type is not array');
    }

    newNode.replaceChildByIndex(index, jsonToMerkleTree(value, hashFn));
  },

  // set object key, value
  'setObjectByKey': ({
    newNode,
    hashFn,
  }, key, value) => {
    if (newNode.data.type !== 'object') {
      throw new Error('node type is not object');
    }

    if (typeof key !== 'string') {
      throw new Error(`key ${key} is not string type`);
    }

    // check if key exists in map
    // TODO opt map clone
    const keyMap = Object.assign({}, newNode.data.keyMap);

    if (Object.prototype.hasOwnProperty.call(keyMap, key)) {
      // update value
      const keyIndex = keyMap[key];

      // replace child
      newNode.replaceChildByIndex(keyIndex + 1, jsonToMerkleTree(value, hashFn));
    } else {
      const keys = [];
      for (let k in keyMap) {
        keys[keyMap[k] / 2] = k;
      }
      keys.push(key);

      const children = newNode.children.slice(0);
      children.push(jsonToMerkleTree(key, hashFn));
      children.push(jsonToMerkleTree(value, hashFn));

      const keyChildren = [];
      const valueChildren = [];
      for (let i = 0; i < children.length; i++) {
        if (i % 2 === 0) {
          keyChildren.push(children[i]);
        } else {
          valueChildren.push(children[i]);
        }
      }

      const list = keys.map((k, i) => {
        return {
          key: k,
          keyChild: keyChildren[i],
          valueChild: valueChildren[i]
        };
      });
      list.sort((a, b) => {
        return a.key > b.key;
      });

      const newKeyMap = {},
        newChildren = [];
      for (let i = 0; i < list.length; i++) {
        newKeyMap[list[i].key] = i * 2;
        newChildren.push(list[i].keyChild);
        newChildren.push(list[i].valueChild);
      }

      // update data
      newNode.updateData(Object.assign({}, newNode.data, {
        keyMap: newKeyMap,
      }));

      // append key, value two nodes
      newNode.children = newChildren;
      newNode.updateHash();
    }
  },

  // remove object key
  'removeKey': ({
    newNode,
  }, key) => {
    if (newNode.data.type !== 'object') {
      throw new Error('node type is not object');
    }

    const keyMap = newNode.data.keyMap;

    if (!Object.prototype.hasOwnProperty.call(keyMap, key)) {
      return;
    }

    const keyIndex = keyMap[key];

    // update data
    const newKeyMap = {};
    for (let k in keyMap) {
      if (keyMap[k] < keyIndex) {
        newKeyMap[k] = keyMap[k];
      }
      if (keyMap[k] > keyIndex + 1) {
        newKeyMap[k] = keyMap[k] - 2;
      }
    }
    newNode.updateData(Object.assign({}, newNode.data, {
      keyMap: newKeyMap,
    }));

    // remove key and value node
    newNode.removeChildByRange(keyIndex, keyIndex + 1);
  },
};

const getWriteOps = () => {
  const ops = {};

  for (let key in handlers) {
    ops[key] = function(jsonPath, ...v) {
      if (this.readonly) {
        throw new Error('try create a new merkle tree for modifying in read only mode');
      }

      return this.createNewImmutableJsonMerkleTreeByJsonPath(jsonPath, handlers[key], ...v);
    };
  }

  // set('a', 1)
  // set(['a', 'b'], [1, 2])
  ops.set = function(jsonPath, value) {
    if (this.readonly) {
      throw new Error('try create a new merkle tree for modifying in read only mode');
    }

    // batch setting
    if (Array.isArray(jsonPath)) {
      let immutableMerkleTree = this;
      for (let i = 0; i < jsonPath.length; i++) {
        immutableMerkleTree = immutableMerkleTree.set(jsonPath[i], value[i]);
      }
      return immutableMerkleTree;
    }

    // set item
    if (jsonPath === '' || jsonPath === '.') {
      throw new Error('empty json path');
    }

    const prevJsonPath = back(jsonPath);
    const key = getLast(jsonPath);

    const {
      node,
    } = findNodeByJsonPath(this.immutableTree.root, prevJsonPath);

    if (node.data.type === 'array') {
      return this.createNewImmutableJsonMerkleTreeByJsonPath(prevJsonPath, handlers['updateAt'], Number(key), value);
    }

    if (node.data.type === 'object') {
      return this.createNewImmutableJsonMerkleTreeByJsonPath(prevJsonPath, handlers['setObjectByKey'], key, value);
    }
  };

  return ops;
};

export default getWriteOps();
